import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Snackbar,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Stack,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  SelectChangeEvent,
  Pagination,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  Extension as ExtensionIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Star as StarIcon,
  Category as CategoryIcon,
  TextFields as TextFieldsIcon,
  Numbers as NumbersIcon,
  DateRange as DateRangeIcon,
  CheckBox as CheckBoxIcon,
  RadioButtonChecked as RadioIcon,
  ArrowDropDownCircle as DropdownIcon,
  AttachFile as FileIcon,
  Image as ImageIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  LocationOn as LocationIcon,
  Timer as TimerIcon,
  Subject as TextareaIcon,
  Draw as SignatureIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { BaseFormComponent, FormTemplate } from './types';
import { getFieldFamilyLabel, getTemplateDomainLabel } from './helpers';
import { getFormComponentDisplay, getFormTemplateDisplay } from '../processDisplay';
import { useTranslation } from 'react-i18next';
import TabPanel from './components/TabPanel';
import ComponentCard from './components/ComponentCard';
import TemplateCard from './components/TemplateCard';
import ComponentDialog from './components/ComponentDialog';
import TemplateDialog from './components/TemplateDialog';
import CreateTemplateDialog, { type FormTemplateForEdit } from './components/CreateTemplateDialog';
import CreateComponentDialog from './components/CreateComponentDialog';
import { ConfirmDialog } from '../../../components/primitives';
import { ApiError, processesApi } from '../../../api';
import type { FormComponent as FormComponentDTO, FormTemplate as FormTemplateDTO } from '../../../api/processes';
import { translateApiError } from '../../../i18n/translateApiError';

// ---------------------------------------------------------------------------
// Backend → frontend adapter. BaseFormComponent.icon is a React element
// the mock populated per-row; ComponentCard falls back to <ExtensionIcon />
// when it's absent, so we simply omit it for live rows.
// ---------------------------------------------------------------------------
// Map backend component.name to a meaningful MUI icon.
function iconForName(name: string): React.ReactElement {
  const key = name.toLowerCase();
  if (key.includes('number') || key.includes('currency')) return <NumbersIcon />;
  if (key.includes('currency') || key.includes('money') || key.includes('amount')) return <MoneyIcon />;
  if (key.includes('date')) return <DateRangeIcon />;
  if (key.includes('checkbox')) return <CheckBoxIcon />;
  if (key.includes('radio')) return <RadioIcon />;
  if (key.includes('dropdown') || key.includes('select')) return <DropdownIcon />;
  if (key.includes('file') || key.includes('upload') || key.includes('attach')) return <FileIcon />;
  if (key.includes('image') || key.includes('photo')) return <ImageIcon />;
  if (key.includes('worker') || key.includes('user') || key.includes('person')) return <PersonIcon />;
  if (key.includes('department') || key.includes('team') || key.includes('group')) return <GroupsIcon />;
  if (key.includes('location') || key.includes('address')) return <LocationIcon />;
  if (key.includes('duration') || key.includes('timer')) return <TimerIcon />;
  if (key.includes('signature')) return <SignatureIcon />;
  if (key.includes('textarea') || key.includes('long') || key.includes('paragraph')) return <TextareaIcon />;
  if (key.includes('text')) return <TextFieldsIcon />;
  return <ExtensionIcon />;
}

function toFront(c: FormComponentDTO): BaseFormComponent {
  const runtimeProperties = c.properties && typeof c.properties === 'object' && !Array.isArray(c.properties)
    ? c.properties as Record<string, unknown>
    : {};
  return {
    id: c.id,
    name: c.name,
    fieldType: c.field_type,
    label: c.label,
    icon: iconForName(c.field_type),
    category: (c.category ?? 'Basic') as BaseFormComponent['category'],
    description: c.description ?? '',
    properties: Array.isArray(c.properties)
      ? (c.properties as BaseFormComponent['properties'])
      : [],
    runtimeProperties,
    sourceProperties: c.properties,
    configurable: c.configurable ?? true,
    usageCount: c.usage_count ?? 0,
    isSystem: (c as any).is_system ?? false,
  } as BaseFormComponent;
}

function toFrontTemplate(t: FormTemplateDTO): FormTemplate {
  return {
    id: t.id,
    name: t.name,
    description: t.description ?? '',
    category: (t.category ?? 'General') as FormTemplate['category'],
    icon: iconForName(t.name),  // reuse icon mapping
    difficulty: (t.difficulty ?? 'Medium') as FormTemplate['difficulty'],
    usageCount: t.usage_count ?? 0,
    // ORDER and WIDTH both come from the wire (migration 111), and BOTH are carried
    // through. This used to `.map((item) => item.component_id)`, on the reasoning that
    // "this surface only needs the ids — it counts them and resolves labels". The count
    // and the labels were right; the template-detail dialog downstream needed the span,
    // and by then it was gone, so two fields set to Half rendered full width (owner
    // screenshots 2026-08-28 16:21 + 17:26).
    items: Array.isArray(t.items)
      ? (t.items as Array<{ component_id: string; col_span?: number }>).map((item) => ({
        componentId: item.component_id,
        // A row saved before the migration, or by an older client, has no span. Full
        // width rather than `undefined`, which renders as a one-column sliver.
        colSpan: item.col_span ?? 12,
      }))
      : [],
    estimatedTime: t.estimated_time ? `${t.estimated_time} min` : '—',
    isPopular: t.is_popular ?? false,
    lastUpdated: (t as any).created_at?.slice(0, 10) ?? '',
    isSystem: (t as any).is_system ?? false,
  } as FormTemplate;
}

/** Turn a DISPLAY row back into editor input.
 *
 * `toFront` above is lossy on purpose: `estimatedTime` becomes `"30 min"` / `"—"` for a card, and
 * each item becomes `{ componentId, colSpan }` in camelCase. Neither can be fed to a form —
 * TypeScript caught `string` where a number belongs, which is the cheap version of this bug; the
 * items would have failed SILENTLY, sending `{componentId: …}` to a backend that reads
 * `component_id` and saving a template with no rows.
 *
 * So the editor un-maps rather than reading the display row, and the parse is TOLERANT of the
 * display forms because that is what it receives: `"30 min"` yields 30, `"—"` yields null.
 */
function toEditableTemplate(template: FormTemplate): FormTemplateForEdit {
  const minutes = parseInt(String(template.estimatedTime ?? ''), 10);
  return {
    id: template.id,
    name: template.name,
    description: template.description,
    category: template.category,
    difficulty: template.difficulty,
    estimatedTime: Number.isFinite(minutes) ? minutes : null,
    items: (template.items ?? []).map((item) => ({
      component_id: item.componentId,
      col_span: item.colSpan ?? 12,
    })),
  };
}

const FormComponents: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmall = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMedium = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isLarge = useMediaQuery(theme.breakpoints.between('lg', 'xl'));
  const isExtraLarge = useMediaQuery(theme.breakpoints.up('xl'));

  // Live components from /v1/processes/form-components + templates from
  // /v1/processes/form-templates. System defaults are seeded by
  // scripts/seed_multi_orgs.py with is_system=True.
  const [components, setComponents] = useState<BaseFormComponent[]>([]);
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [comps, tpls] = await Promise.all([
        processesApi.formComponentsApi.list(),
        processesApi.formTemplatesApi.list(),
      ]);
      setComponents(comps.map(toFront));
      setTemplates(tpls.map(toFrontTemplate));
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.loadFailed'));
      setComponents([]);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [componentCategoryFilter, setComponentCategoryFilter] = useState<string>('all');
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState<string>('all');
  const [componentStatusFilter, setComponentStatusFilter] = useState<string>('all');
  const [templateStatusFilter, setTemplateStatusFilter] = useState<string>('all');
  const [componentDialogOpen, setComponentDialogOpen] = useState(false);
  const [createComponentOpen, setCreateComponentOpen] = useState(false);
  const [editingComponent, setEditingComponent] = useState<BaseFormComponent | null>(null);
  const [deletingComponent, setDeletingComponent] = useState<BaseFormComponent | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<FormTemplate | null>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  /** The template the create/edit dialog is EDITING, or `null` when it is creating.
   *
   * Separate from `selectedTemplate` (the read-only preview's subject) so closing the editor
   * returns to the preview rather than to nothing — the admin arrived from the card and the
   * preview is where "did that work?" gets answered. */
  const [editingTemplate, setEditingTemplate] = useState<FormTemplate | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<BaseFormComponent | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  const [componentPage, setComponentPage] = useState(1);
  const [templatePage, setTemplatePage] = useState(1);

  const getItemsPerPage = useMemo(() => {
    if (isExtraSmall) return 6;
    if (isSmall) return 8;
    if (isMedium) return 9;
    if (isLarge) return 12;
    if (isExtraLarge) return 15;
    return 9;
  }, [isExtraSmall, isSmall, isMedium, isLarge, isExtraLarge]);

  React.useEffect(() => {
    setComponentPage(1);
    setTemplatePage(1);
  }, [getItemsPerPage]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSearchTerm('');
    setComponentPage(1);
    setTemplatePage(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setComponentPage(1);
    setTemplatePage(1);
  };

  const handleComponentCategoryFilterChange = (event: SelectChangeEvent<string>) => {
    setComponentCategoryFilter(event.target.value);
    setComponentPage(1);
  };

  const handleTemplateCategoryFilterChange = (event: SelectChangeEvent<string>) => {
    setTemplateCategoryFilter(event.target.value);
    setTemplatePage(1);
  };

  const handleComponentStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setComponentStatusFilter(event.target.value);
    setComponentPage(1);
  };

  const handleTemplateStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setTemplateStatusFilter(event.target.value);
    setTemplatePage(1);
  };

  const handleOpenComponentDialog = (component: BaseFormComponent) => {
    setSelectedComponent(component);
    setComponentDialogOpen(true);
  };

  const handleCloseComponentDialog = () => {
    setComponentDialogOpen(false);
    setSelectedComponent(null);
  };

  const handleEditComponent = (component: BaseFormComponent) => {
    handleCloseComponentDialog();
    setEditingComponent(component);
    setCreateComponentOpen(true);
  };

  const handleDeleteComponent = (component: BaseFormComponent) => {
    handleCloseComponentDialog();
    setDeletingComponent(component);
  };

  const confirmDeleteComponent = async () => {
    if (!deletingComponent) return;
    try {
      await processesApi.formComponentsApi.delete(deletingComponent.id);
      setToast(t('processes:formComponents.componentDeleted', { name: deletingComponent.label }));
      setDeletingComponent(null);
      await fetchAll();
    } catch (caught) {
      setToast(caught instanceof ApiError
        ? translateApiError(caught, t)
        : t('processes:formComponents.componentDeleteFailed'));
      throw caught;
    }
  };

  // Deliberately the SAME flow as confirmDeleteComponent above — ConfirmDialog,
  // translateApiError, toast, re-fetch — rather than a second confirmation pattern.
  // Everything for this existed except the button: the endpoint, its 403 on is_system,
  // and `formTemplatesApi.delete`, which had ZERO call sites in src/.
  const handleDeleteTemplate = (template: FormTemplate) => {
    handleCloseTemplateDialog();
    setDeletingTemplate(template);
  };

  const confirmDeleteTemplate = async () => {
    if (!deletingTemplate) return;
    try {
      await processesApi.formTemplatesApi.delete(deletingTemplate.id);
      setToast(t('processes:formComponents.templateDeleted', { name: deletingTemplate.name }));
      setDeletingTemplate(null);
      await fetchAll();
    } catch (caught) {
      setToast(caught instanceof ApiError
        ? translateApiError(caught, t)
        : t('processes:formComponents.templateDeleteFailed'));
      throw caught;
    }
  };

  const handleOpenTemplateDialog = (template: FormTemplate) => {
    setSelectedTemplate(template);
    setTemplateDialogOpen(true);
  };

  const handleCloseTemplateDialog = () => {
    setTemplateDialogOpen(false);
    setSelectedTemplate(null);
  };

  const filteredComponents = useMemo(() => {
    return components.filter((component) => {
      const searchMatch =
        getFormComponentDisplay(t, component).label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getFormComponentDisplay(t, component).description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        component.name.toLowerCase().includes(searchTerm.toLowerCase());
      const categoryMatch = componentCategoryFilter === 'all' || component.category === componentCategoryFilter;
      const statusMatch =
        componentStatusFilter === 'all' ||
        (componentStatusFilter === 'configurable' && component.configurable) ||
        (componentStatusFilter === 'non-configurable' && !component.configurable);
      return searchMatch && categoryMatch && statusMatch;
    });
  }, [components, searchTerm, componentCategoryFilter, componentStatusFilter, t]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((template: FormTemplate) => {
      const searchMatch =
        getFormTemplateDisplay(t, template).name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getFormTemplateDisplay(t, template).description.toLowerCase().includes(searchTerm.toLowerCase());
      const categoryMatch = templateCategoryFilter === 'all' || template.category === templateCategoryFilter;
      const statusMatch =
        templateStatusFilter === 'all' ||
        (templateStatusFilter === 'popular' && template.isPopular) ||
        (templateStatusFilter === 'regular' && !template.isPopular);
      return searchMatch && categoryMatch && statusMatch;
    });
  }, [searchTerm, templateCategoryFilter, templateStatusFilter, templates, t]);

  const paginatedComponents = useMemo(() => {
    const startIndex = (componentPage - 1) * getItemsPerPage;
    return filteredComponents.slice(startIndex, startIndex + getItemsPerPage);
  }, [filteredComponents, componentPage, getItemsPerPage]);

  const paginatedTemplates = useMemo(() => {
    const startIndex = (templatePage - 1) * getItemsPerPage;
    return filteredTemplates.slice(startIndex, startIndex + getItemsPerPage);
  }, [filteredTemplates, templatePage, getItemsPerPage]);

  const componentPageCount = Math.ceil(filteredComponents.length / getItemsPerPage);
  const templatePageCount = Math.ceil(filteredTemplates.length / getItemsPerPage);

  const componentCategories = ['all', ...Array.from(new Set(components.map((c) => c.category)))];
  const templateCategories = ['all', ...Array.from(new Set(templates.map((t: FormTemplate) => t.category)))];

  const popularTemplates = paginatedTemplates.filter((t: FormTemplate) => t.isPopular);
  const regularTemplates = paginatedTemplates.filter((t: FormTemplate) => !t.isPopular);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px' }}>
      {/* Header */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>
            {t('processes:formComponents.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('processes:formComponents.subtitle')}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Tooltip title={t('common:actions.refresh')}>
            <span>
              <IconButton size="small" onClick={fetchAll} disabled={loading}
                sx={{ mr: 1, border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => { setEditingComponent(null); setCreateComponentOpen(true); }}
          >
            {t('processes:formComponents.newComponent')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DescriptionIcon />}
            size="small"
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
            onClick={() => setCreateTemplateOpen(true)}
          >
            {t('processes:formComponents.newTemplate')}
          </Button>
        </Stack>
      </Box>

      <Alert icon={false} severity="info" sx={{ width: '100%', mb: 2 }}>
        {t('processes:formComponents.systemInfo')}
      </Alert>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {/* Compact stats line — 13" laptop friendly, no vertical bloat */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          <ExtensionIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.5, color: 'primary.main' }} />
          <strong>{components.length}</strong> {t('processes:formComponents.statsComponents')}
        </Typography>
        <Typography variant="caption" color="text.disabled">·</Typography>
        <Typography variant="body2" color="text.secondary">
          <DescriptionIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.5, color: 'info.main' }} />
          <strong>{templates.length}</strong> {t('processes:formComponents.statsTemplates')}
        </Typography>
        <Typography variant="caption" color="text.disabled">·</Typography>
        <Typography variant="body2" color="text.secondary">
          {components.reduce((sum, c) => sum + c.usageCount, 0).toLocaleString(i18n.language)} {t('processes:formComponents.componentUses')}
        </Typography>
        <Typography variant="caption" color="text.disabled">·</Typography>
        <Typography variant="body2" color="text.secondary">
          {templates.reduce((sum: number, t: FormTemplate) => sum + t.usageCount, 0).toLocaleString(i18n.language)} {t('processes:formComponents.templateUses')}
        </Typography>
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label={t('processes:liveDisplay.ui.formTabsAria')}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              minHeight: 48,
              color: 'text.secondary',
              '&.Mui-selected': { color: 'primary.main', fontWeight: 700 },
            },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          <Tab label={t('processes:formComponents.componentLibrary')} id="form-components-tab-0" aria-controls="form-components-tabpanel-0" />
          <Tab label={t('processes:formComponents.presetTemplates')} id="form-components-tab-1" aria-controls="form-components-tabpanel-1" />
        </Tabs>
      </Box>

      {/* Shared Search and Filter Bar */}
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder={t(tabValue === 0 ? 'processes:formComponents.searchComponentPlaceholder' : 'processes:formComponents.searchTemplatePlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} /> }}
          />
          {/* The category filter names WHICH vocabulary it is filtering, because it filters
              two different things depending on the tab: a component's field type, or a
              template's business area. It carried one label ("Category") for both, so the
              same control silently changed meaning — and its options changed with it, from
              Basic/Choice/Amount to HR/Finance/Operations. One sense per control
              (svdy-rules §18.4). */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>
              {t(tabValue === 0
                ? 'processes:formComponents.fieldFamilyLabel'
                : 'processes:formComponents.templateDomainLabel')}
            </InputLabel>
            <Select
              value={tabValue === 0 ? componentCategoryFilter : templateCategoryFilter}
              label={t(tabValue === 0
                ? 'processes:formComponents.fieldFamilyLabel'
                : 'processes:formComponents.templateDomainLabel')}
              onChange={tabValue === 0 ? handleComponentCategoryFilterChange : handleTemplateCategoryFilterChange}
            >
              {(tabValue === 0 ? componentCategories : templateCategories).map((category: string) => (
                <MenuItem key={category} value={category}>
                  {category === 'all'
                    ? t(tabValue === 0
                      ? 'processes:formComponents.allFieldTypes'
                      : 'processes:formComponents.allDomains')
                    : (tabValue === 0
                      ? getFieldFamilyLabel(t, category)
                      : getTemplateDomainLabel(t, category))}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('processes:formComponents.statusLabel')}</InputLabel>
            <Select
              value={tabValue === 0 ? componentStatusFilter : templateStatusFilter}
              label={t('processes:formComponents.statusLabel')}
              onChange={tabValue === 0 ? handleComponentStatusFilterChange : handleTemplateStatusFilterChange}
            >
              <MenuItem value="all">{t('processes:formComponents.allStatus')}</MenuItem>
              {tabValue === 0 ? (
                <>
                  <MenuItem value="configurable">{t('processes:formComponents.configurable')}</MenuItem>
                  <MenuItem value="non-configurable">{t('processes:formComponents.nonConfigurable')}</MenuItem>
                </>
              ) : (
                <>
                  <MenuItem value="popular">{t('processes:formComponents.popular')}</MenuItem>
                  <MenuItem value="regular">{t('processes:formComponents.regular')}</MenuItem>
                </>
              )}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {tabValue === 0
              ? t('processes:formComponents.componentsFound', { count: filteredComponents.length })
              : t('processes:formComponents.templatesFound', { count: filteredTemplates.length })}
          </Typography>
          {(tabValue === 0 ? componentPageCount : templatePageCount) > 1 && (
            <Pagination
              count={tabValue === 0 ? componentPageCount : templatePageCount}
              page={tabValue === 0 ? componentPage : templatePage}
              onChange={tabValue === 0
                ? (_: React.ChangeEvent<unknown>, v: number) => setComponentPage(v)
                : (_: React.ChangeEvent<unknown>, v: number) => setTemplatePage(v)}
              size="small"
              color="primary"
              showFirstButton
              showLastButton
            />
          )}
        </Box>
      </Box>

      {/* Tab 0: Component Library */}
      <TabPanel value={tabValue} index={0}>
        {filteredComponents.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ExtensionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('processes:formComponents.noMatchingComponents')}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t('processes:formComponents.adjustCriteria')}
            </Typography>
          </Box>
        )}
        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
          {paginatedComponents.map((component) => (
            <Grid
              item
              key={component.id}
              xs={12} sm={6} md={4} lg={3}
              sx={{ display: 'flex' }}
            >
              <ComponentCard component={component} onOpen={handleOpenComponentDialog} />
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Tab 1: Form Templates */}
      <TabPanel value={tabValue} index={1}>
        {filteredTemplates.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <AssignmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('processes:formComponents.noMatchingTemplates')}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t('processes:formComponents.adjustCriteria')}
            </Typography>
          </Box>
        )}
        {popularTemplates.length > 0 && (
          <Box mb={3}>
            <Typography
              variant="subtitle2"
              component="h2"
              sx={{ display: 'flex', alignItems: 'center', mb: 1.5, fontWeight: 700, color: 'text.primary', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              <StarIcon sx={{ color: theme.palette.warning.main, mr: 0.75, fontSize: 16 }} />
              {t('processes:formComponents.popularSection')}
            </Typography>
            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
              {popularTemplates.map((template: FormTemplate) => (
                <Grid
                  item
                  key={template.id}
                  xs={12} sm={6} md={4} lg={3}
                  sx={{ display: 'flex' }}
                >
                  <TemplateCard template={template} onOpen={handleOpenTemplateDialog} />
                </Grid>
              ))}
            </Grid>
            {regularTemplates.length > 0 && (
              <Typography
                variant="subtitle2"
                component="h2"
                sx={{ mt: 3, mb: 1.5, display: 'flex', alignItems: 'center', fontWeight: 700, color: 'text.primary', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                <CategoryIcon sx={{ color: theme.palette.info.main, mr: 0.75, fontSize: 16 }} />
                {t('processes:formComponents.allTemplatesSection')}
              </Typography>

            )}
          </Box>
        )}
        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
          {regularTemplates.map((template: FormTemplate) => (
            <Grid
              item
              key={template.id}
              xs={12} sm={6} md={4} lg={3}
              sx={{ display: 'flex',  }}
            >
              <TemplateCard template={template} onOpen={handleOpenTemplateDialog} />
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <ComponentDialog
        open={componentDialogOpen}
        component={selectedComponent}
        onClose={handleCloseComponentDialog}
        onEdit={handleEditComponent}
        onDelete={handleDeleteComponent}
      />

      <TemplateDialog
        open={templateDialogOpen}
        template={selectedTemplate}
        onClose={handleCloseTemplateDialog}
        onOpenComponent={(comp) => {
          handleCloseTemplateDialog();
          handleOpenComponentDialog(comp);
        }}
        allComponents={components}
        onDelete={handleDeleteTemplate}
        onEdit={(template) => {
          setEditingTemplate(template);
          setCreateTemplateOpen(true);
        }}
      />

      <CreateTemplateDialog
        open={createTemplateOpen}
        components={components}
        template={editingTemplate && toEditableTemplate(editingTemplate)}
        onClose={() => {
          setCreateTemplateOpen(false);
          // CLEARED on every exit, or the next "New template" opens pre-filled with the last thing
          // that was edited — which reads as the product having lost the admin's click.
          setEditingTemplate(null);
        }}
        onCreated={() => {
          setToast(t(editingTemplate
            ? 'processes:formComponents.templateUpdated'
            : 'processes:formComponents.templateCreated'));
          setEditingTemplate(null);
          fetchAll();
        }}
      />

      <CreateComponentDialog
        open={createComponentOpen}
        component={editingComponent}
        onClose={() => { setCreateComponentOpen(false); setEditingComponent(null); }}
        onSaved={() => {
          setToast(t(editingComponent
            ? 'processes:formComponents.componentUpdated'
            : 'processes:formComponents.componentCreated'));
          fetchAll();
        }}
      />

      <ConfirmDialog
        open={!!deletingComponent}
        tone="danger"
        title={t('processes:formComponents.deleteComponentTitle')}
        description={t('processes:formComponents.deleteComponentDescription')}
        subject={deletingComponent?.label}
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('processes:formComponents.deletingComponent')}
        onConfirm={confirmDeleteComponent}
        onClose={() => setDeletingComponent(null)}
      />

      <ConfirmDialog
        open={!!deletingTemplate}
        tone="danger"
        title={t('processes:formComponents.deleteTemplateTitle')}
        description={t('processes:formComponents.deleteTemplateDescription')}
        subject={deletingTemplate?.name}
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('processes:formComponents.deletingTemplate')}
        onConfirm={confirmDeleteTemplate}
        onClose={() => setDeletingTemplate(null)}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FormComponents;
